import { atom } from "jotai";
import { togglesAtomResettable } from "../diagramming";

export interface TreatmentsResponse {
  [flagName: string]: string[];
}

// Convert camelCase back to snake_case for Split.io API
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

// Atom that returns query string for string-based flags
export const stringFlagsToQueryStringAtom = atom((get) => {
  const toggles = get(togglesAtomResettable);

  // Filter for flags that can have multiple treatment values (not just boolean on/off)
  //
  // Flag types in our system:
  // - Boolean flags: true/false - these use ToggleSwitch in DevTools
  // - Boolean flags with config: { isOn: true/false, config?: {...} } - these use ToggleSwitch in DevTools
  // - String flags: "v3" - these can have multiple values like ["v2", "v3", "v4"] and use Select dropdown
  // - String flags with config: { treatment: "v3", config?: {...} } - these also use Select dropdown
  //
  // We only fetch treatments for string and string flags with config since boolean flags don't need them
  const stringFlagsArray = Object.entries(toggles)
    .filter(
      ([, value]) =>
        typeof value === "string" ||
        (typeof value === "object" && value !== null && "treatment" in value),
    )
    .map(([key]) => key);

  if (stringFlagsArray.length === 0) {
    return "";
  }

  // Convert camelCase keys to snake_case for the API call
  const snakeCaseFlags = stringFlagsArray.map((flag) => toSnakeCase(flag));

  // Build query string with snake_case flag names
  return snakeCaseFlags
    .map((flag) => `flags=${encodeURIComponent(flag)}`)
    .join("&");
});

// Fetches all possible treatments for string-based flags
export const stringFlagTreatmentsAtom_SUSPENDS = atom<
  Promise<TreatmentsResponse>
>(async (get) => {
  const queryString = get(stringFlagsToQueryStringAtom);

  if (!queryString) {
    return {};
  }

  // Fetch treatments from our API endpoint
  const response = await fetch(
    `/api/canvasui/toggles/treatments?${queryString}`,
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch treatments for query "${queryString}": ${response.statusText}`,
    );
  }

  const treatments = await response.json();

  // Convert the response keys back to camelCase
  const camelCaseTreatments: TreatmentsResponse = {};
  Object.entries(treatments).forEach(([snakeCaseKey, value]) => {
    // Convert snake_case back to camelCase
    const camelCaseKey = snakeCaseKey.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    camelCaseTreatments[camelCaseKey] = value as string[];
  });

  return camelCaseTreatments;
});
